import { useCallback, useState } from 'react';
import { Severity } from '@sentry/types';

import { SentryLogger } from '../../../../../../../utils/SentryLogger';

import { InvoiceNanoID, InvoiceID } from '../../../../../invoicesTypes';
import { AvBillingInfoPaymentMethods } from '../../../../../../avBillingInfos/avBillingInfosTypes';

import {
  FETCH_CACHE_INVOICE_QUERY,
  FetchCacheInvoiceQueryResponse
} from '../../../../../queries/fetchCacheInvoice.query';

import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';

import { useCacheInvoice } from '../../../../../hooks/useCacheInvoice';

import {
  PayoneerCheckoutAttemptPaymentDataLinksSelf,
  PayoneerCheckoutAttemptPaymentDataLongId
} from '../../../../../../payoneerCheckoutAttempts/payoneerCheckoutAttemptsTypes';
import { PayoneerAttemptPaymentUrl } from '../../../../../../payoneerAttempts/payoneerAttemptsTypes';

import { useCreatePayoneerAttempt } from '../../../../../../payoneerAttempts/hooks/useCreatePayoneerAttempt';
import { useCreatePayoneerV4Attempt } from '../../../../../../payoneerV4Attempts/hooks/useCreatePayoneerV4Attempt';
import { useCreatePayoneerCheckoutAttempt } from '../../../../../../payoneerCheckoutAttempts/hooks/useCreatePayoneerCheckoutAttempt';

import { InvoiceCache } from '../../../../../InvoiceCache';
import { InvoicesPermissions } from '../../../../../invoicesConstants';
import { PAYONEER_CHECKOUT_SDK_VERSION } from '../../../../../../payoneerCheckout/utils/PayoneerCheckoutPayment/payoneerCheckoutPaymentConstants';

interface UsePayInvoiceModalButtonOptions {
  invoiceNanoId: InvoiceNanoID;
}

const sentryLogger = new SentryLogger('pay-invoice');

function usePayInvoiceModalButton({
  invoiceNanoId
}: UsePayInvoiceModalButtonOptions) {
  const currentUser = useCurrentUser();

  const usePayoneerV4Button = currentUser.hasPermissions(
    InvoicesPermissions.READ_INVOICE_PAYONEER_V4_PAY_BUTTON
  );

  const [refetchingInvoice, setRefetchingInvoice] = useState<boolean>(false);

  const [payoneerCheckoutLongId, setPayoneerCheckoutLongId] =
    useState<PayoneerCheckoutAttemptPaymentDataLongId>(null);

  const [payoneerCheckoutListUrl, setPayoneerCheckoutListUrl] =
    useState<PayoneerCheckoutAttemptPaymentDataLinksSelf>(null);

  const [payoneerPaymentUrl, setPayoneerPaymentUrl] =
    useState<PayoneerAttemptPaymentUrl>(null);

  const [payoneerV4PaymentUrl, setPayoneerV4PaymentUrl] =
    useState<PayoneerAttemptPaymentUrl>(null);

  const { invoice, invoiceError, invoiceLoading, refetchInvoice } =
    useCacheInvoice<FetchCacheInvoiceQueryResponse>({
      invoiceUuid: invoiceNanoId,
      query: FETCH_CACHE_INVOICE_QUERY,
      cacheKey: InvoiceCache.showCacheKey(),
      options: {
        enabled: false,
        enabledPlaceholder: false
      }
    });

  const {
    createPayoneerCheckoutAttemptLoading,
    createPayoneerCheckoutAttemptErrorMessage,
    createPayoneerCheckoutAttempt
  } = useCreatePayoneerCheckoutAttempt();

  const {
    createPayoneerAttemptLoading,
    createPayoneerAttemptErrorMessage,
    createPayoneerAttempt
  } = useCreatePayoneerAttempt();

  const {
    createPayoneerV4AttemptLoading,
    createPayoneerV4AttemptErrorMessage,
    createPayoneerV4Attempt
  } = useCreatePayoneerV4Attempt();

  const handleInitializePayoneerCheckoutPayment = useCallback<
    (invoiceId: InvoiceID) => void
  >(
    async (invoiceId) => {
      const integration =
        PAYONEER_CHECKOUT_SDK_VERSION === 'web'
          ? 'embedded'
          : 'selective_native';

      sentryLogger.breadcrumb('initializing payoneer checkout attempt', {
        invoiceId,
        integration,
        sdkVersion: PAYONEER_CHECKOUT_SDK_VERSION
      });

      try {
        const response = await createPayoneerCheckoutAttempt({
          payoneerCheckoutAttempt: { invoiceId, integration }
        });

        const longId = response?.paymentData?.identification?.long_id;
        const listUrl = response?.paymentData?.links?.self;

        if (!longId && !listUrl) {
          sentryLogger.captureMessage(
            'usePayInvoiceModalButton: payoneer checkout response has no payment data',
            {
              stage: 'payoneer-checkout-empty-payment-data',
              tags: { 'payoneer-checkout-sdk': PAYONEER_CHECKOUT_SDK_VERSION },
              extra: {
                invoiceId,
                integration,
                hasLongId: Boolean(longId),
                hasListUrl: Boolean(listUrl)
              }
            }
          );
        }

        setPayoneerCheckoutLongId(longId);
        setPayoneerCheckoutListUrl(listUrl);
      } catch (error) {
        sentryLogger.captureException(error, {
          stage: 'payoneer-checkout-attempt-failed',
          tags: { 'payoneer-checkout-sdk': PAYONEER_CHECKOUT_SDK_VERSION },
          extra: { invoiceId, integration }
        });
      }
    },
    [createPayoneerCheckoutAttempt]
  );

  const handleInitializePayoneerPayment = useCallback<
    (invoiceId: InvoiceID) => void
  >(
    async (invoiceId) => {
      sentryLogger.breadcrumb('initializing payoneer attempt', { invoiceId });

      try {
        const response = await createPayoneerAttempt({
          payoneerAttempt: { invoiceId }
        });

        if (response?.paymentUrl) {
          setPayoneerPaymentUrl(response.paymentUrl);
          return;
        }

        sentryLogger.captureMessage(
          'usePayInvoiceModalButton: payoneer attempt response has no paymentUrl',
          {
            stage: 'payoneer-attempt-empty-payment-url',
            extra: { invoiceId }
          }
        );
      } catch (error) {
        sentryLogger.captureException(error, {
          stage: 'payoneer-attempt-failed',
          extra: { invoiceId }
        });
      }
    },
    [createPayoneerAttempt]
  );

  const handleInitializePayoneerV4Payment = useCallback<
    (invoiceId: InvoiceID) => void
  >(
    async (invoiceId) => {
      sentryLogger.breadcrumb('initializing payoneer v4 attempt', {
        invoiceId
      });

      try {
        const response = await createPayoneerV4Attempt({
          payoneerV4Attempt: { invoiceId }
        });

        if (response?.paymentUrl) {
          setPayoneerV4PaymentUrl(response.paymentUrl);
          return;
        }

        sentryLogger.captureMessage(
          'usePayInvoiceModalButton: payoneer v4 attempt response has no paymentUrl',
          {
            stage: 'payoneer-v4-attempt-empty-payment-url',
            extra: { invoiceId }
          }
        );
      } catch (error) {
        sentryLogger.captureException(error, {
          stage: 'payoneer-v4-attempt-failed',
          extra: { invoiceId }
        });
      }
    },
    [createPayoneerV4Attempt]
  );

  const handleSubmit = useCallback<() => Promise<void>>(async () => {
    if (usePayoneerV4Button && payoneerV4PaymentUrl) {
      sentryLogger.breadcrumb('submit: opening payoneer v4 payment url');
      window.open(payoneerV4PaymentUrl);
      return;
    }

    if (!usePayoneerV4Button && payoneerPaymentUrl) {
      sentryLogger.breadcrumb('submit: opening payoneer payment url');
      window.open(payoneerPaymentUrl);
      return;
    }

    sentryLogger.breadcrumb(
      'submit: no payment url available',
      {
        usePayoneerV4Button,
        hasPayoneerPaymentUrl: Boolean(payoneerPaymentUrl),
        hasPayoneerV4PaymentUrl: Boolean(payoneerV4PaymentUrl)
      },
      Severity.Warning
    );
    return;
  }, [usePayoneerV4Button, payoneerPaymentUrl, payoneerV4PaymentUrl]);

  const handleOpen = useCallback<() => void>(async () => {
    sentryLogger.breadcrumb('open: refetching invoice', { invoiceNanoId });

    try {
      setPayoneerCheckoutLongId(null);
      setPayoneerCheckoutListUrl(null);
      setPayoneerPaymentUrl(null);
      setPayoneerV4PaymentUrl(null);
      setRefetchingInvoice(true);
      const refetchResponse = await refetchInvoice();
      setRefetchingInvoice(false);

      const fetchedInvoice = refetchResponse?.data?.invoice;
      if (!fetchedInvoice) {
        sentryLogger.captureMessage(
          'usePayInvoiceModalButton: refetchInvoice returned no invoice',
          {
            stage: 'open-no-invoice',
            extra: { invoiceNanoId }
          }
        );
        return;
      }

      const paymentMethod = fetchedInvoice.avInvoiceBillingInfo?.paymentMethod;

      sentryLogger.breadcrumb('open: invoice fetched', {
        invoiceId: fetchedInvoice.id,
        paymentMethod,
        usePayoneerV4Button
      });

      if (paymentMethod === AvBillingInfoPaymentMethods.PAYONEER_CHECKOUT) {
        handleInitializePayoneerCheckoutPayment(fetchedInvoice.id);
      }
      if (
        paymentMethod === AvBillingInfoPaymentMethods.PAYONEER &&
        !usePayoneerV4Button
      ) {
        handleInitializePayoneerPayment(fetchedInvoice.id);
      }
      if (
        paymentMethod === AvBillingInfoPaymentMethods.PAYONEER &&
        usePayoneerV4Button
      ) {
        handleInitializePayoneerV4Payment(fetchedInvoice.id);
      }
    } catch (e) {
      setRefetchingInvoice(false);
      sentryLogger.captureException(e, {
        stage: 'open-failed',
        extra: { invoiceNanoId }
      });
    }
  }, [
    invoiceNanoId,
    usePayoneerV4Button,
    handleInitializePayoneerCheckoutPayment,
    handleInitializePayoneerPayment,
    handleInitializePayoneerV4Payment,
    refetchInvoice
  ]);

  const isPayoneerCheckoutForm =
    invoice?.avInvoiceBillingInfo?.paymentMethod ===
    AvBillingInfoPaymentMethods.PAYONEER_CHECKOUT;

  const isStripeForm =
    invoice?.avInvoiceBillingInfo?.paymentMethod ===
    AvBillingInfoPaymentMethods.STRIPE;

  const isPaypalForm =
    invoice?.avInvoiceBillingInfo?.paymentMethod ===
    AvBillingInfoPaymentMethods.PAYPAL;

  return {
    invoice,
    errorMessage:
      createPayoneerCheckoutAttemptErrorMessage ||
      createPayoneerAttemptErrorMessage ||
      createPayoneerV4AttemptErrorMessage ||
      invoiceError,
    isLoading:
      createPayoneerCheckoutAttemptLoading ||
      createPayoneerAttemptLoading ||
      createPayoneerV4AttemptLoading ||
      invoiceLoading ||
      refetchingInvoice,
    isPayoneerCheckoutForm,
    isStripeForm,
    isPaypalForm,
    payoneerCheckoutLongId,
    payoneerCheckoutListUrl,
    handleOpen,
    handleSubmit
  };
}

export default usePayInvoiceModalButton;
